// src/components/tutor/mobile/MobileTutorSubjects.js
// Two views:
//   selectedCategory === null  → category list (passed via props)
//   selectedCategory !== null  → subject detail for that category
// Navigation is driven by MobileTutorProfile so the SubPageWrapper
// title and back-button behaviour change correctly.
import React, { useState, useEffect, useMemo } from "react";
import { auth, db } from "../../../firebaseConfig";
import {
  collection,
  query,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  arrayUnion,
  arrayRemove,
  orderBy,
} from "firebase/firestore";
import { LuCheck, LuSearch } from "react-icons/lu";

function normalizeCategoryId(name) {
  return (name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export default function MobileTutorSubjects({ selectedCategory, onSelectCategory }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  /* ─── Fetch current tutor ─────────────────────────────────────────── */
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        if (snap.exists()) {
          setCurrentUser({ uid: snap.id, ...snap.data(), subjects: snap.data().subjects || [] });
        }
      })
      .catch(console.error);
  }, []);

  /* ─── Real-time categories ───────────────────────────────────────────── */
  useEffect(() => {
    const q = query(collection(db, "subject_categories"), orderBy("order", "asc"));
    return onSnapshot(q, (snap) => {
      const cats = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cats.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(cats);
    });
  }, []);

  /* ─── Real-time subjects ─────────────────────────────────────────────── */
  useEffect(() => {
    return onSnapshot(query(collection(db, "subjects")), (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name,
        rootSubject: d.data().rootSubject || "Uncategorized",
        tutorIds: d.data().tutorIds || [],
        order: d.data().order || 0,
        ...d.data(),
      }));
      setSubjects(list);
      setLoading(false);
    });
  }, []);

  /* ─── Toggle assignment ──────────────────────────────────────────────── */
  const toggleAssignment = async (subject) => {
    if (!currentUser) return;
    const assigned =
      subject.tutorIds?.includes(currentUser.uid) ||
      currentUser.subjects?.includes(subject.name);
    try {
      if (assigned) {
        await updateDoc(doc(db, "subjects", subject.id), { tutorIds: arrayRemove(currentUser.uid) });
        await updateDoc(doc(db, "users", currentUser.uid), { subjects: arrayRemove(subject.name) });
        setCurrentUser((p) => ({ ...p, subjects: p.subjects.filter((s) => s !== subject.name) }));
      } else {
        await updateDoc(doc(db, "subjects", subject.id), { tutorIds: arrayUnion(currentUser.uid) });
        await updateDoc(doc(db, "users", currentUser.uid), { subjects: arrayUnion(subject.name) });
        setCurrentUser((p) => ({ ...p, subjects: [...(p.subjects || []), subject.name] }));
      }
    } catch (err) {
      console.error("Error toggling subject:", err);
    }
  };

  /* ─── Category rows data ─────────────────────────────────────────────── */
  const categoryRows = useMemo(() => {
    const sortedCats = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    const mainBuckets = new Map();
    sortedCats.forEach((cat) => {
      mainBuckets.set(cat.id, { name: cat.name, subjects: [] });
    });
    const extraBuckets = new Map();
    const unc = { name: "Uncategorized", subjects: [] };

    subjects.forEach((sub) => {
      const rawRoot = (sub.rootSubject || "Uncategorized").trim();
      const id = normalizeCategoryId(rawRoot) || "uncategorized";
      if (id === "uncategorized") { unc.subjects.push(sub); return; }
      if (mainBuckets.has(id)) { mainBuckets.get(id).subjects.push(sub); return; }
      if (!extraBuckets.has(id)) extraBuckets.set(id, { name: rawRoot, subjects: [] });
      extraBuckets.get(id).subjects.push(sub);
    });

    const rows = [];
    const push = (bucket) => {
      if (!bucket.subjects.length) return;
      bucket.subjects.sort((a, b) => (a.order || 0) - (b.order || 0));
      const qualified = currentUser
        ? bucket.subjects.filter(
            (s) =>
              s.tutorIds?.includes(currentUser.uid) ||
              currentUser.subjects?.includes(s.name)
          ).length
        : 0;
      rows.push({ name: bucket.name, total: bucket.subjects.length, qualified });
    };

    for (const [, b] of mainBuckets) push(b);
    Array.from(extraBuckets.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(push);
    push(unc);
    return rows;
  }, [subjects, categories, currentUser]);

  /* ─── Detail subjects (for selected category) ────────────────────────── */
  const detailSubjects = useMemo(() => {
    if (!selectedCategory) return [];
    const lower = searchTerm.toLowerCase();
    const allInCat = subjects
      .filter((s) => (s.rootSubject || "Uncategorized") === selectedCategory)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    return searchTerm
      ? allInCat.filter((s) => s.name.toLowerCase().includes(lower))
      : allInCat;
  }, [selectedCategory, subjects, searchTerm]);

  const detailQualified = useMemo(() => {
    if (!selectedCategory || !currentUser) return 0;
    return subjects.filter(
      (s) =>
        (s.rootSubject || "Uncategorized") === selectedCategory &&
        (s.tutorIds?.includes(currentUser.uid) || currentUser.subjects?.includes(s.name))
    ).length;
  }, [selectedCategory, subjects, currentUser]);

  const totalQualified = useMemo(() => {
    if (!currentUser) return 0;
    return subjects.filter(
      (s) =>
        s.tutorIds?.includes(currentUser.uid) || currentUser.subjects?.includes(s.name)
    ).length;
  }, [subjects, currentUser]);

  /* ─── Loading ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="mts-loading">
        <div className="mts-spinner" />
        <p className="mts-loading-text">Loading subjects…</p>
      </div>
    );
  }

  /* ─── Category detail view ───────────────────────────────────────────── */
  if (selectedCategory) {
    const totalInCat = subjects.filter(
      (s) => (s.rootSubject || "Uncategorized") === selectedCategory
    ).length;

    return (
      <div className="mts-detail-page">
        {/* Stats bar */}
        <div className="mts-detail-stats">
          <span className="mts-detail-stat-text">
            {detailQualified} of {totalInCat} subjects qualified
          </span>
        </div>

        {/* Search */}
        <div className="mts-search-wrap">
          <LuSearch size={14} className="mts-search-icon" />
          <input
            className="mts-search-input"
            placeholder="Search subjects…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="mts-clear-btn" onClick={() => setSearchTerm("")}>✕</button>
          )}
        </div>

        {/* Subjects */}
        <div className="mts-subject-card">
          {detailSubjects.length === 0 && (
            <p className="mts-detail-empty">No subjects found.</p>
          )}
          {detailSubjects.map((subject, idx) => {
            const assigned =
              currentUser &&
              (subject.tutorIds?.includes(currentUser.uid) ||
                currentUser.subjects?.includes(subject.name));
            const isLast = idx === detailSubjects.length - 1;
            return (
              <button
                key={subject.id}
                className={`mts-subject-row ${assigned ? "mts-subject-row--on" : ""} ${isLast ? "mts-subject-row--last" : ""}`}
                onClick={() => toggleAssignment(subject)}
                aria-pressed={!!assigned}
              >
                <span className="mts-subject-name">{subject.name}</span>
                <span className={`mts-check-box ${assigned ? "mts-check-box--on" : ""}`}>
                  {assigned && <LuCheck size={13} strokeWidth={2.5} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* ─── Category list view ─────────────────────────────────────────────── */
  return (
    <div className="mts-list-page">
      {/* Qualified summary */}
      {totalQualified > 0 && (
        <p className="mts-summary-label">
          You are qualified to teach <strong>{totalQualified}</strong> subject{totalQualified !== 1 ? "s" : ""} across {categoryRows.filter((r) => r.qualified > 0).length} categories.
        </p>
      )}

      {/* Category cards */}
      <div className="mts-cat-card">
        {categoryRows.length === 0 && (
          <p className="mts-detail-empty">No categories available.</p>
        )}
        {categoryRows.map((row, idx) => (
          <button
            key={row.name}
            className={`mts-cat-row ${idx === categoryRows.length - 1 ? "mts-cat-row--last" : ""}`}
            onClick={() => onSelectCategory(row.name)}
          >
            <div className="mts-cat-row-info">
              <span className="mts-cat-row-name">{row.name}</span>
              <span className="mts-cat-row-total">{row.total} subjects</span>
            </div>
            <div className="mts-cat-row-right">
              {row.qualified > 0 && (
                <span className="mts-cat-qualified-badge">
                  {row.qualified}/{row.total}
                </span>
              )}
              <span className="mts-cat-chevron">›</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
